import React, { useEffect, useState } from 'react';
import { render } from 'react-dom';
import { useDispatch } from 'react-redux';
import { resetChatAction } from 'chat/actions';
import { FILE_UPLOAD_CHAT_ROOM } from 'chat/constants';
import './UploadPopup.css';
import { DISEASE } from 'utils/constants/disease';
import { POPUP } from 'utils/constants/popup';
import { CHAT_DISEASES } from 'utils/constants/chat';
import { QUESTION_ID } from 'modules/questionnaire/questionIds';
import app from 'new-ui/app';
import FilesUploader from 'new-ui/Components/FilesUploader';
import { UPLOADER_STATES } from 'new-ui/Components/FilesUploader/FileUploaderConstants';
import Button from 'new-ui/Components/Button';
import PropTypes from 'prop-types';
import { useTranslation } from 'utils/modifiedTranslation';
import { JOB_STATUS } from 'modules/medicalFiles/constants';
import ArrowCirlce from 'assets/icons/arrow-circle-right.svg';
import { getDrugName, questionsNormalizer, waitFor } from 'new-ui/functions';
import { UPLOAD_INTAKE_QUESTIONS } from 'new-ui/Components/Intake/questions';
import Intake, { EXPAND_FIRST } from 'new-ui/Components/Intake';
import { QUESTION_TYPE } from 'new-ui/Components/QuestionRenderer/constants';
import { ChatButton } from 'components/ChatButton/ChatButton';
import useRecaptcha from 'utils/hooks/useRecaptcha';
import { useAuth } from 'utils/hooks/useAuth';
import { getUserCondition } from 'modules/user/utils';
import { getErrorMessages } from 'modules/medicalFiles/utils';
import { AiAnimate } from "./AiAnimate";
import { showSkip } from "./UploadFormUtils";
import { GA } from "./UploadFormConstants";

const {
  treatments: {
    subQuestionIds: {
      drugs_received: DRUGS_RECEIVED_ID,
    },
  },
} = QUESTION_ID;

const uploader = {};

let user;
let results;

export const UploadForm = ({
  onSuccess,
  onUpdate,
  onBeforeIntake,
  condition,
  onUploadedFilesChange,
  onGotoResultsAction,
  uploadFiles,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();

  const [uploaderState, setUploaderState] = useState(UPLOADER_STATES.SELECT);
  const { recaptcha, getRecaptchaTokenAsync } = useRecaptcha();
  const [errorMessages, setErrorMessages] = useState([]);
  const [finishUploading, setIsFinishUploading] = useState(false);
  const [drugsRecevedFromJob, setDrugsRecevedFromJob] = useState(null);
  const [isPlaying, setIsPlaying] = useState(true);

  useEffect(() => {
    if (finishUploading) {
      if (isPlaying) {
        showSkip();
      } else {
        uploader.setState(UPLOADER_STATES.CUSTOM1)
      }
    }
  }, [finishUploading, isPlaying]);

  useEffect(() => () => {
    dispatch(resetChatAction(FILE_UPLOAD_CHAT_ROOM));
  }, [dispatch]);

  const gotoNextStep = () => {
    uploader.setState(UPLOADER_STATES.CUSTOM1);
  };

  const isChatButtonShown = isAuthenticated && CHAT_DISEASES.includes(condition);

  const header = () => {
    switch (uploaderState) {
      default:
      case UPLOADER_STATES.SELECT:
        return <div className="upload-popup-title">Got a medical record?</div>;
      case UPLOADER_STATES.MULTI:
        return <div className="upload-popup-title">Is that all?</div>;
      case UPLOADER_STATES.UPLOADING:
        return (
          <div className="animate">
            <AiAnimate
              onEnd={() => {
                setIsPlaying(false);
                if (finishUploading) gotoNextStep();
              }}
              onSkip={gotoNextStep}
            />
          </div>
        );
      case UPLOADER_STATES.ERROR:
        return (
          <div className="ngs-upload-error">
            {errorMessages?.map((it, idx) => (!idx ? <h3>{it}</h3> : <p>{it}</p>))}
          </div>
        );
      case UPLOADER_STATES.ANALYZING:
        return null;
      case UPLOADER_STATES.SUCCESS:
        return null;
      case UPLOADER_STATES.CUSTOM1:
        const fetch = async () => {
          const extractedCondition = getUserCondition(user);

          if (condition && condition !== extractedCondition) {
            const $el = document.querySelector('.files-uploader');
            render(
              (
                <>
                  <div className="file-uploader-title">
                    Cannot extract
                    <span role="img" aria-label="smily">😅</span>
                  </div>
                  <div className="file-uploader-text">
                    The indication found in the extracted file was not the same as the one in the current profile
                  </div>
                  <Button
                    title="Close"
                    action={() => {
                      app.setPopup(false);
                    }}
                  />
                </>
              ),
              $el,
            );
            return;
          }

          const questions = questionsNormalizer(UPLOAD_INTAKE_QUESTIONS, extractedCondition);
          app.sendGoogleAnalyticsEvent(GA.category, GA.events.reviewing, { name: 'Upload file attrubutes reviewing viewed' });

          switch (extractedCondition) {
            case DISEASE.LUNG:
              questions.push('braf_mutation', 'egft_mut', 'kras_mut', 'erbb2_her2_mut', 'met_mut');
              break;
            case DISEASE.CRC_2:
            case DISEASE.CRC:
              questions.push('kras_mut');
              break;
            case DISEASE.MELANOMA:
            case DISEASE.MELANOMA_2:
              questions.push('braf_mutation');
              break;
            default:
              break;
          }

          questions.unshift(DRUGS_RECEIVED_ID);

          if (onBeforeIntake) await onBeforeIntake(questions, results, user);

          const DashboardButton = () => (
            <>
              <div className="upload-intake-parent-button">
                <Button
                  forwardIcon={ArrowCirlce}
                  action={async () => {
                    app.sendGoogleAnalyticsEvent(GA.category, GA.events.confirmaed, { name: 'Upload file attrubutes confirmed' });

                    app.store.dispatch(resetChatAction(FILE_UPLOAD_CHAT_ROOM));
                    if (onSuccess) {
                      await onSuccess(user, questions)
                    }
                  }}
                  title={t('upload.confirm_and_see_btn_text')}
                />
              </div>
            </>
          );
          let appendQuestions;
          let expandQuestions;
          let expandFirstQuestion = true;
          if (user.condition_profile?.drugs_received?.length) {
            expandFirstQuestion = false;
            appendQuestions = [
              {
                id: DRUGS_RECEIVED_ID,
                section: 'MY_CANCER',
                type: QUESTION_TYPE.DRUGS_RECEIVED,
                valueRenderer: () => {
                  if (!drugsRecevedFromJob || !drugsRecevedFromJob?.length) return 'No drugs found';
                  return drugsRecevedFromJob.map((drug) => window.t(getDrugName(drug))).join(', ');
                },
              },
            ];
            expandQuestions = [DRUGS_RECEIVED_ID];
          }
          const $div = (
            <div>
              <div className="upload-popup-title">{t('upload.popup_title')}</div>
              <div className="upload-intake-block">
                <div className="upload-intake-parent">
                  <Intake
                    id="uploadPopup"
                    showOnlyAnswered
                    customQuestions={questions}
                    expandQuestions={expandQuestions}
                    appendQuestions={appendQuestions}
                    gotoResultsAction={onGotoResultsAction}
                    flatMode
                    onUpdate={onUpdate}
                    user={user}
                    {...(expandFirstQuestion ? { expand: EXPAND_FIRST } : {})}
                  />
                  {isChatButtonShown && (
                    <ChatButton isChildPopup popupType={POPUP.CHAT_UPLOAD} text="Understand your profile" />
                  )}
                  <DashboardButton />
                </div>
              </div>
            </div>
          );

          waitFor(() => document.querySelector('.ai-upload-intake'), () => {
            render($div, document.querySelector('.ai-upload-intake'));
          }, () => {});
        };

        fetch();
        return <div className="ai-upload-intake" />;
    }
  };

  const back = () => {
    uploader.setFiles([...uploader.files.filter((a) => !a.error)]);
    uploader.setState(UPLOADER_STATES.MULTI);
  };

  const errorFooter = () => (
    <div>
      <Button title="Upload another file" action={back} />
    </div>
  );

  const isErrored = (files = uploader.files) => {
    const erroredFiles = files.filter((a) => a.error);
    return erroredFiles.length === files.length;
  };

  const footer = () => {
    switch (uploaderState) {
      default:
      case UPLOADER_STATES.SELECT:
        return null;
      case UPLOADER_STATES.UPLOADING:
        return null;
      case UPLOADER_STATES.ERROR:
        return errorFooter();
      case UPLOADER_STATES.SUCCESS:
        return isErrored() ? errorFooter() : null;
    }
  };

  const filerenderer = () => {
    switch (uploaderState) {
      default:
      case UPLOADER_STATES.SELECT:
      case UPLOADER_STATES.UPLOADING:
      case UPLOADER_STATES.ERROR:
        return null;
      case UPLOADER_STATES.SUCCESS:
        return null;
    }
  };

  const uploadError = () => {
    uploader.setState(UPLOADER_STATES.ERROR);
  };

  const upload = async () => {
    const files = [...uploader.files];
    uploader.setState(UPLOADER_STATES.UPLOADING);

    uploadFiles({
      files: [...uploader.files],
      getRecaptchaTokenAsync,
      onUpload: async (r) => {
        if (r?.status === JOB_STATUS.COMPLETED) {
          // todo document indication is not same as user indication error
          user = r.profile;
          results = r;
        }

        if (r?.status === JOB_STATUS.ERROR || r?.status === JOB_STATUS.FILE_ERROR) {
          uploadError();
          setErrorMessages(getErrorMessages(r, t));
          return;
        }

        const { profile = {}, id: jobId, files: uploadedFiles } = r;
        const conditionProfile = profile?.condition_profile ?? {};
        const drugs = conditionProfile?.drugs_received;

        setDrugsRecevedFromJob(drugs);
        setIsFinishUploading(true);
        user = profile;
        uploader.setFiles(files);

        if (isPlaying) {
          showSkip();
        } else {
          uploader.setState(UPLOADER_STATES.CUSTOM1)
        }

        onUploadedFilesChange(
          uploadedFiles,
          { jobId },
        );
      },
      onError: () => {
        uploader.setState(UPLOADER_STATES.ERROR);
      },
    });
  };

  return (
    <div className="upload-popup">
      {recaptcha}
      <FilesUploader
        isUploadPopup
        isMulti
        onstatechange={(state) => {
          const $img = document.querySelector('.popup-theme-middle .popup-header img');
          if ($img) {
            $img.classList.remove('hidden');
            if (state === UPLOADER_STATES.UPLOADING) {
              $img.classList.add('hidden');
            }
          }

          setUploaderState(state);
        }}
        header={header()}
        footer={footer()}
        filerenderer={filerenderer}
        uploader={uploader}
        onselect={() => upload()}
      />
    </div>
  );
};

UploadForm.propTypes = {
  onSuccess: PropTypes.func,
  onUpdate: PropTypes.func,
  condition: PropTypes.any,
  onBeforeIntake: PropTypes.func,
  onUploadedFilesChange: PropTypes.func.isRequired,
  onGotoResultsAction: PropTypes.func,
  uploadFiles: PropTypes.func.isRequired,
};
